import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package, CheckCircle, XCircle, UserPlus
} from 'lucide-react';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useAuth } from '../../../contexts/AuthContext';

export function UserRegisterPanel() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState({ licensed: 0, limit: 100 });
  const [regRequests, setRegRequests] = useState([]);
  const [sortKey, setSortKey] = useState('user_id');
  const [sortDir, setSortDir] = useState('asc');
  const confirm = useConfirm();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, licRes, regRes] = await Promise.all([
        api.get('/api/admin/users', { params: { search, limit: 100 } }),
        api.get('/api/admin/users/license-info/info').catch(() => ({ data: { licensed: 0, limit: 100 } })),
        api.get('/api/auth/registration-requests').catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data.users || []);
      setTotal(usersRes.data.total || 0);
      if (licRes.data) setLicenseInfo(licRes.data);
      setRegRequests((regRes.data || []).filter(r => r.status === 'pending'));
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load users'); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }); }
    catch { return d; }
  };

  // If editing/adding, show the full form
  if (editingUser !== null) {
    return <UserEditForm user={editingUser === 'new' ? null : editingUser} onClose={() => { setEditingUser(null); fetchUsers(); }} licenseInfo={licenseInfo} />;
  }

  return (
    <div>
      <div className="flex flex-col gap-2 mb-4">
        <div>
          <h1 className="text-2xl font-bold">User Maintenance</h1>
          <p className="text-sm text-muted-foreground">User Register · Total records: {total}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="page-header-actions">
          <Button onClick={() => setEditingUser('new')} data-testid="add-new-user-btn">
            <Plus className="h-4 w-4 mr-1" /> Add New User
          </Button>
        </div>
      </div>
      {/* Pending Registration Requests */}
      {regRequests.length > 0 && (
        <div className="mb-5 border border-amber-200 rounded-lg bg-amber-50/50 p-4" data-testid="registration-requests">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-800">New User Requests ({regRequests.length})</h3>
          </div>
          <div className="space-y-2">
            {regRequests.map(r => (
              <div key={r.email} className="flex items-center gap-3 bg-white rounded-lg border px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{r.first_name} {r.last_name}</p>
                  <p className="text-xs text-muted-foreground">{r.email}{r.phone ? ` — ${r.phone}` : ''}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {r.requested_at ? new Date(r.requested_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : ''}
                  </p>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                  try {
                    const res = await api.post(`/api/auth/registration-requests/${encodeURIComponent(r.email)}/approve`);
                    toast.success(res.data.message);
                    fetchUsers();
                  } catch (e) { toast.error(getApiErrorMessage(e, 'Approve failed')); }
                }} data-testid={`approve-${r.email}`}>
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="destructive" onClick={async () => {
                  if (!await confirm(`Reject the registration request from ${r.first_name} ${r.last_name}?`, { title: 'Reject Request', destructive: true })) return;
                  try {
                    await api.post(`/api/auth/registration-requests/${encodeURIComponent(r.email)}/reject`, { reason: '' });
                    toast.success('Request rejected');
                    fetchUsers();
                  } catch (e) { toast.error(getApiErrorMessage(e, 'Reject failed')); }
                }} data-testid={`reject-${r.email}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={e => { e.preventDefault(); fetchUsers(); }} className="flex items-center gap-2 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="w-48" />
        <Button type="submit" size="sm"><Search className="h-4 w-4 mr-1" /> Search</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => { setSearch(''); setTimeout(fetchUsers, 100); }}><X className="h-4 w-4 mr-1" /> Clear</Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Export</Button>
      </form>
      {loading ? <LoadingSpinner /> : users.length === 0 ? <EmptyState title="No users found" /> : (() => {
        const handleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };
        const sorted = [...users].sort((a, b) => {
          let va = a[sortKey] || ''; let vb = b[sortKey] || '';
          if (sortKey === 'last_login') { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; }
          else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
          return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
        });
        const SH = ({ label, field, className = '' }) => (
          <th className={`text-left px-3 py-2 font-medium cursor-pointer select-none hover:bg-muted transition-colors ${className}`} onClick={() => handleSort(field)}>
            <span className="flex items-center gap-1">{label}{sortKey === field && <span className="text-[10px]">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}</span>
          </th>
        );
        return (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <SH label="User ID" field="user_id" />
                <SH label="First Name" field="first_name" />
                <SH label="Last Name" field="last_name" />
                <SH label="User Type" field="user_type" />
                <SH label="Email" field="email" />
                <th className="text-center px-3 py-2 font-medium">User Away</th>
                <th className="text-center px-3 py-2 font-medium">Licensed</th>
                <SH label="Last Login" field="last_login" />
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(u => (
                <tr key={u._id || u.user_id} className="border-t hover:bg-accent/50 cursor-pointer" onClick={() => setEditingUser(u)}>
                  <td className="px-3 py-2 font-mono text-primary">{u.user_id || u._id?.slice(-8)}</td>
                  <td className="px-3 py-2">{u.first_name || ''}</td>
                  <td className="px-3 py-2">{u.last_name || ''}</td>
                  <td className="px-3 py-2">{u.user_type || 'Others'}</td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2 text-center">{u.is_away ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-center">{u.is_licensed ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{formatDate(u.last_login)}</td>
                  <td className="px-2"><Button size="sm" variant="ghost" data-testid={`edit-user-${u.user_id || u._id}`}><Settings className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}
    </div>
  );
}

export function UserEditForm({ user, onClose, licenseInfo }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const isNew = !user;
  const [form, setForm] = useState({
    user_id: user?.user_id || '', first_name: user?.first_name || '', last_name: user?.last_name || '',
    email: user?.email || '', job_title: user?.job_title || '', extension: user?.extension || '',
    telephone: user?.telephone || '', mobile_country: user?.mobile_country || '', mobile: user?.mobile || '',
    is_licensed: user?.is_licensed ?? true, is_away: user?.is_away || false,
    user_type: user?.user_type || 'Buyer', rfq_access: user?.rfq_access || false,
    is_vendor_manager: user?.is_vendor_manager || false,
    is_supplier_price_manager: user?.is_supplier_price_manager || false,
    is_accounts_manager: user?.is_accounts_manager || false,
    account_payable_sync: user?.account_payable_sync || false,
    password: '',
    permission_role_id: user?.permission_role_id || '',
    default_codes: user?.default_codes || {},
    allowed_sites: user?.allowed_sites || [],
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ details: true, roles: true, defaults: false, signature: false, sites: true });
  const [pwMenuOpen, setPwMenuOpen] = useState(false);
  const pwMenuRef = useRef(null);
  const { labels: expLabels } = useExpenditureLabels();

  // Close password menu on click outside
  useEffect(() => {
    const handler = (e) => { if (pwMenuRef.current && !pwMenuRef.current.contains(e.target)) setPwMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const [permRoles, setPermRoles] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [sitesEnabled, setSitesEnabled] = useState(false);

  useEffect(() => {
    api.get('/api/admin/permission-roles').then(r => setPermRoles(r.data.roles || [])).catch(() => {});
    Promise.all([
      api.get('/api/sites/module/status').catch(() => ({ data: { enabled: false } })),
      api.get('/api/sites').catch(() => ({ data: { data: [] } })),
    ]).then(([statusRes, sitesRes]) => {
      setSitesEnabled(statusRes.data.enabled);
      setAvailableSites((sitesRes.data.data || []).filter(s => s.active));
    });
  }, []);

  const u = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const uDefault = (f, v) => setForm(p => ({ ...p, default_codes: { ...p.default_codes, [f]: v } }));
  const toggle = (s) => setExpanded(p => ({ ...p, [s]: !p[s] }));

  const handleSave = async () => {
    // Validate mandatory fields
    const missing = [];
    if (!form.user_id) missing.push('User ID');
    if (!form.email) missing.push('Email');
    if (!form.first_name) missing.push('First Name');
    if (!form.last_name) missing.push('Last Name');
    if (!form.telephone) missing.push('Telephone');
    if (!form.permission_role_id) missing.push('Permission Role');
    if (isNew && !form.password) missing.push('Initial Password');
    if (missing.length > 0) {
      toast.error(`Missing mandatory fields: ${missing.join(', ')}`);
      return;
    }
    // Validate password complexity if provided
    if (form.password) {
      const pw = form.password;
      if (pw.length < 8 || !/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/[0-9]/.test(pw) || !/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pw)) {
        toast.error('Password does not meet complexity requirements. Check the indicators below the password field.');
        return;
      }
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/api/admin/users', form);
        toast.success('User created');
      } else {
        // Strip internal fields that shouldn't be sent back
        const { _id, id, _version, password_hash, created_at, last_login, reset_token, reset_token_expires, ...payload } = form;
        await api.put(`/api/admin/users/${user.user_id || user._id}/full`, payload);
        toast.success('User updated');
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/admin/users/${user.user_id || user._id}`);
      toast.success('User deleted');
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cannot delete user'); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }); }
    catch { return d; }
  };

  const SectionHeader = ({ id, label }) => (
    <button onClick={() => toggle(id)} className="w-full flex items-center justify-between p-3 bg-muted/30 border rounded-t-lg text-sm font-semibold hover:bg-muted/50" data-testid={`user-section-${id}`}>
      {label}
      <ChevronRight className={`h-4 w-4 transition-transform ${expanded[id] ? 'rotate-90' : ''}`} />
    </button>
  );

  const handleSendPassword = async (sendEmail = true) => {
    if (!user?.user_id) return;
    try {
      const res = await api.post(`/api/admin/users/${user.user_id}/send-password`);
      if (res.data.sent) {
        toast.success(res.data.message);
      } else if (res.data.reset_url) {
        // Email not configured — show the reset URL to copy
        toast.info('Email not configured. Copy this reset link:', { duration: 3000 });
        navigator.clipboard.writeText(res.data.reset_url);
        toast.success('Reset link copied to clipboard', { duration: 8000 });
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send reset link'); }
  };

  return (
    <div data-testid="user-edit-form">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">User Maintenance</h1>
          <p className="text-sm text-muted-foreground">User Register — {isNew ? 'Add New User' : `Editing: ${user.email}`}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4 mr-1" /> Back to List</Button>
      </div>

      {/* Section 1: User Details */}
      <div className="border rounded-lg mb-4">
        <SectionHeader id="details" label="User Details" />
        {expanded.details && (
          <div className="p-4">
            <div className="grid grid-cols-3 gap-4">
              {/* Left column */}
              <div className="col-span-2 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs"><span className="text-red-500">User ID *</span></Label>
                    <Input value={form.user_id} onChange={e => u('user_id', e.target.value)} disabled={!isNew} className="text-sm" data-testid="user-form-id" /></div>
                  <div className="space-y-1"><Label className="text-xs">Extension</Label>
                    <Input value={form.extension} onChange={e => u('extension', e.target.value)} className="text-sm" data-testid="user-form-ext" /></div>
                  <div className="space-y-1"><Label className="text-xs"><span className="text-red-500">Telephone *</span></Label>
                    <Input value={form.telephone} onChange={e => u('telephone', e.target.value)} className="text-sm" data-testid="user-form-tel" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs"><span className="text-red-500">First Name *</span></Label>
                    <Input value={form.first_name} onChange={e => u('first_name', e.target.value)} className="text-sm" data-testid="user-form-fname" /></div>
                  <div className="space-y-1"><Label className="text-xs">Mobile</Label>
                    <div className="flex gap-1">
                      <Input value={form.mobile_country} onChange={e => u('mobile_country', e.target.value)} className="w-16 text-sm" placeholder="Code" />
                      <Input value={form.mobile} onChange={e => u('mobile', e.target.value)} className="text-sm" placeholder="Number" data-testid="user-form-mobile" />
                    </div></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs"><span className="text-red-500">Last Name *</span></Label>
                    <Input value={form.last_name} onChange={e => u('last_name', e.target.value)} className="text-sm" data-testid="user-form-lname" /></div>
                  <div className="space-y-1"><Label className="text-xs">Account Payable Sync</Label>
                    <div className="flex items-center gap-2 pt-1">
                      <Switch checked={form.account_payable_sync} onCheckedChange={v => u('account_payable_sync', v)} data-testid="user-form-ap-sync" />
                      <span className={`text-xs font-medium ${form.account_payable_sync ? 'text-green-600' : ''}`}>{form.account_payable_sync ? 'Yes' : 'No'}</span>
                    </div></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs"><span className="text-red-500">Email *</span></Label>
                    <Input value={form.email} onChange={e => u('email', e.target.value)} disabled={!isNew} className="text-sm" data-testid="user-form-email" /></div>
                  <div><Label className="text-xs">Last Login</Label><p className="text-xs text-muted-foreground pt-1">{formatDate(user?.last_login)}</p></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Job Title</Label>
                  <DataLookupField value={form.job_title} onChange={v => u('job_title', v)} apiEndpoint="/api/admin/job-titles" dataTestId="user-form-jobtitle" />
                </div>
                {isNew && <div className="space-y-1"><Label className="text-xs"><span className="text-red-500">Initial Password *</span></Label>
                  <Input type="password" value={form.password} onChange={e => u('password', e.target.value)} placeholder="Enter password" className="text-sm" data-testid="user-form-password" />
                  {form.password && (() => {
                    const pw = form.password;
                    const checks = [
                      { ok: pw.length >= 8, label: 'At least 8 characters' },
                      { ok: /[A-Z]/.test(pw), label: 'Uppercase letter (A-Z)' },
                      { ok: /[a-z]/.test(pw), label: 'Lowercase letter (a-z)' },
                      { ok: /[0-9]/.test(pw), label: 'Number (0-9)' },
                      { ok: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pw), label: 'Special character (!@#$%...)' },
                    ];
                    const allOk = checks.every(c => c.ok);
                    return (
                      <div className="mt-1.5 space-y-0.5">
                        {checks.map(c => (
                          <div key={c.label} className={`flex items-center gap-1.5 text-[10px] ${c.ok ? 'text-green-600' : 'text-red-400'}`}>
                            {c.ok ? <span>&#10003;</span> : <span>&#10005;</span>} {c.label}
                          </div>
                        ))}
                        {allOk && <p className="text-green-600 text-[10px] font-semibold mt-1">Password meets all requirements</p>}
                      </div>
                    );
                  })()}
                  {!form.password && <p className="text-[10px] text-muted-foreground mt-1">Must contain: 8+ chars, uppercase, lowercase, number, and special character</p>}
                </div>}
              </div>
              {/* Right column: License & User Type */}
              <div className="space-y-4">
                <div className="border rounded-lg p-3">
                  <h4 className="text-xs font-semibold mb-3">User License ({licenseInfo.licensed} / {licenseInfo.limit})</h4>
                  {licenseInfo.licensed >= licenseInfo.limit && !form.is_licensed && (
                    <p className="text-[10px] text-red-500 mb-2 font-semibold">License limit reached ({licenseInfo.limit}). Cannot enable more licenses.</p>
                  )}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3"><Label className="w-20 text-xs">Licensed</Label>
                      <Switch checked={form.is_licensed} onCheckedChange={v => {
                        if (v && !user?.is_licensed && licenseInfo.licensed >= licenseInfo.limit) {
                          toast.error(`License limit reached (${licenseInfo.limit}). Cannot enable more licenses.`);
                          return;
                        }
                        u('is_licensed', v);
                      }} data-testid="user-form-licensed" />
                      <span className={`text-xs font-medium ${form.is_licensed ? 'text-green-600' : ''}`}>{form.is_licensed ? 'Yes' : 'No'}</span></div>
                    <div className="flex items-center gap-3"><Label className="w-20 text-xs">User Away</Label>
                      <Switch checked={form.is_away} onCheckedChange={v => u('is_away', v)} data-testid="user-form-away" />
                      <span className={`text-xs font-medium ${form.is_away ? 'text-green-600' : ''}`}>{form.is_away ? 'Yes' : 'No'}</span></div>
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <h4 className="text-xs font-semibold mb-2">User Type</h4>
                  <select value={form.user_type} onChange={e => u('user_type', e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="user-form-usertype">
                    <option value="Others">Others</option>
                    <option value="Buyer">Buyer</option>
                    <option value="Requestor">Requestor</option>
                    <option value="Senior Buyer">Senior Buyer</option>
                  </select>
                </div>
                <div className="relative group/vm inline-flex items-center gap-2 cursor-pointer px-1">
                  <input type="checkbox" checked={form.is_vendor_manager || false} onChange={e => u('is_vendor_manager', e.target.checked)} className="h-4 w-4" data-testid="user-form-vendor-manager" />
                  <span className="text-xs font-medium">Vendor Manager</span>
                  <div className="absolute left-0 top-full mt-1 z-50 w-72 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/vm:opacity-100 group-hover/vm:visible transition-all duration-200 pointer-events-none leading-relaxed">
                    <strong>Vendor Manager</strong> — This user is responsible for managing the supplier onboarding process. They will receive email notifications whenever a new supplier registers on the Supplier Portal and needs approval. They can approve or reject supplier registrations directly from the email link or from the Pending Suppliers dashboard widget. All supplier-related correspondence is copied to users with this role.
                  </div>
                </div>
                <div className="relative group/pm inline-flex items-center gap-2 cursor-pointer px-1">
                  <input type="checkbox" checked={form.is_supplier_price_manager || false} onChange={e => u('is_supplier_price_manager', e.target.checked)} className="h-4 w-4" data-testid="user-form-price-manager" />
                  <span className="text-xs font-medium">Supplier Price Manager</span>
                  <div className="absolute left-0 top-full mt-1 z-50 w-72 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/pm:opacity-100 group-hover/pm:visible transition-all duration-200 pointer-events-none leading-relaxed">
                    <strong>Supplier Price Manager</strong> — This user has the authority to review, approve, or reject price change requests submitted by suppliers through the Supplier Portal. When a supplier requests a new price for an item (individually or as a bulk percentage update), users with this role can review the request and either approve (which updates the master item price) or reject it. They can also perform bulk approve/reject actions across multiple price requests.
                  </div>
                </div>
                <div className="relative group/am inline-flex items-center gap-2 cursor-pointer px-1">
                  <input type="checkbox" checked={form.is_accounts_manager || false} onChange={e => u('is_accounts_manager', e.target.checked)} className="h-4 w-4" data-testid="user-form-accounts-manager" />
                  <span className="text-xs font-medium">Accounts Manager</span>
                  <div className="absolute left-0 top-full mt-1 z-50 w-72 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/am:opacity-100 group-hover/am:visible transition-all duration-200 pointer-events-none leading-relaxed">
                    <strong>Accounts Manager</strong> — This user receives all invoice-related notifications. When a supplier uploads an invoice through the Supplier Portal, users with this role are notified via email with full invoice details, PO matching status (exact match, mismatch, or no PO found), and a direct link to review the invoice. They are the primary point of contact for accounts payable processing and invoice approval workflows.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Roles */}
      <div className="border rounded-lg mb-4">
        <SectionHeader id="roles" label="Roles" />
        {expanded.roles && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold"><span className="text-red-500">Permission Role *</span></Label>
                <select value={form.permission_role_id || ''} onChange={e => u('permission_role_id', e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="user-permission-role">
                  <option value="">— No Permission Role —</option>
                  {permRoles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.member_count || 0} members)</option>)}
                </select>
                <p className="text-xs text-muted-foreground">{permRoles.find(r => r.id === form.permission_role_id)?.description || 'Select a role to assign permissions'}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground border-t pt-2">Manage roles in System Admin → Roles section. Users inherit all permissions from their assigned role. Approval routing is handled by System Approval Rules.</p>
          </div>
        )}
      </div>

      {/* Section 3: User Default Codes */}
      <div className="border rounded-lg mb-4">
        <SectionHeader id="defaults" label="User Default Codes" />
        {expanded.defaults && (
          <div className="p-4 grid grid-cols-2 gap-4">
            {[
              {k:'delivery_point', l:'Delivery Point', api:'/api/data/delivery-points'},
              {k:'gl', l:expLabels.exp_code_4, api:'/api/account-codes/4'},
              {k:'invoice_point', l:'Invoice Point', api:'/api/data/invoice-points'},
              {k:'property_code', l:expLabels.exp_code_5, api:'/api/account-codes/5'},
              {k:'document_type', l:'Document Type', api:'/api/data/document-types'},
              {k:'nominal_code', l:expLabels.exp_code_6, api:'/api/account-codes/6'},
              {k:'cost_centre', l:expLabels.cost_centre, api:'/api/account-codes/7'},
              {k:'category', l:'Category', api:'/api/data/categories'},
              {k:'account', l:expLabels.exp_code_1, api:'/api/account-codes/1'},
              {k:'budget', l:'Budget', type:'select'},
              {k:'restaurant', l:expLabels.exp_code_2, api:'/api/account-codes/2'},
              {k:'incoterms', l:'Incoterms', type:'incoterms'},
              {k:'department_code', l:expLabels.exp_code_3, api:'/api/account-codes/3'},
              {k:'shipping_method', l:'Shipping Method', type:'shipping'},
            ].map(f => (
              <div key={f.k}>
                {f.api ? (
                  <DataLookupField label={f.l} value={form.default_codes[f.k]||''} onChange={v=>uDefault(f.k,v)} apiEndpoint={f.api} dataTestId={`default-${f.k}`} />
                ) : f.type === 'select' || f.type === 'incoterms' || f.type === 'shipping' ? (
                  <div className="space-y-1">
                    <Label className="text-xs">{f.l}</Label>
                    <select value={form.default_codes[f.k]||''} onChange={e=>uDefault(f.k,e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`default-${f.k}`}>
                      <option value="">-- Select --</option>
                      {f.type==='incoterms' && ['EXW','FCA','FAS','FOB','CFR','CIF','CPT','CIP','DAP','DPU','DDP'].map(t=><option key={t}>{t}</option>)}
                      {f.type==='shipping' && ['Air Freight','Sea Freight','Road Freight','Rail Freight','Express/Courier','Parcel Post','Hand Delivery','Collect','LTL','FTL','Dropship'].map(m=><option key={m}>{m}</option>)}
                      {f.type==='select' && ['In Budget','Out of Budget','Over Budget'].map(b=><option key={b}>{b}</option>)}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-1"><Label className="text-xs">{f.l}</Label>
                    <Input value={form.default_codes[f.k]||''} onChange={e=>uDefault(f.k,e.target.value)} className="text-sm" data-testid={`default-${f.k}`} /></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 4: User Signature */}
      <div className="border rounded-lg mb-4">
        <SectionHeader id="signature" label="User Signature" />
        {expanded.signature && (
          <div className="p-4 grid grid-cols-2 gap-6">
            <div>
              <div className="border-2 border-dashed rounded-lg p-6 text-center bg-muted/10 mb-3">
                <div className="w-full h-16 flex items-center justify-center bg-muted/20 rounded mb-2">
                  <span className="text-muted-foreground text-xs">Signature Preview</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">Drag & drop file here.</p>
                <p className="text-xs text-muted-foreground mb-2">-- or --</p>
                <Button variant="outline" size="sm">Select File</Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">Upload</Button>
                <Button variant="destructive" size="sm"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1 pt-2">
              <p>(The image in the display area is exactly how it will appear on your documents(maximum size 250Kb))</p>
              <p>Best Fit Height is 60 pixels</p>
              <p>Best Fit Width is 200 pixels Size = 2Kb</p>
            </div>
          </div>
        )}
      </div>

      {/* Section 5: Site Access — only when Sites module is enabled */}
      {sitesEnabled && availableSites.length > 0 && (
        <div className="border rounded-lg mb-4">
          <SectionHeader id="sites" label={`Site Access (${form.allowed_sites.length} of ${availableSites.length})`} />
          {expanded.sites && (
            <div className="p-4">
              <p className="text-xs text-muted-foreground mb-3">Select which sites this user can access. If none selected, admins see all data.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {availableSites.map(s => (
                  <label key={s.site_id} className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                    form.allowed_sites.includes(s.site_id) ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:bg-accent/30'
                  }`}>
                    <input type="checkbox" checked={form.allowed_sites.includes(s.site_id)} onChange={() => {
                      const next = form.allowed_sites.includes(s.site_id)
                        ? form.allowed_sites.filter(id => id !== s.site_id)
                        : [...form.allowed_sites, s.site_id];
                      u('allowed_sites', next);
                    }} className="h-4 w-4 rounded" />
                    <div>
                      <span className="text-sm font-medium">{s.name}</span>
                      {s.code && <span className="text-xs text-muted-foreground ml-1">({s.code})</span>}
                      {s.city && <p className="text-[10px] text-muted-foreground">{s.city}{s.country ? `, ${s.country}` : ''}</p>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button onClick={handleSave} disabled={saving} data-testid="user-save-btn">
          <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
        </Button>
        {!isNew && (
          <Button variant="secondary" onClick={() => handleSendPassword()} data-testid="user-send-password-btn">
            <Lock className="h-4 w-4 mr-1" /> Send Reset Link
          </Button>
        )}
        {!isNew && (
          <Button variant="destructive" onClick={handleDelete} data-testid="user-delete-btn">
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        )}
        <Button variant="outline" onClick={onClose}>
          <X className="h-4 w-4 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}
